import React, { createContext, useContext, useReducer, useEffect, useState, useCallback, useRef } from 'react';
import api from '../api/api';

const CartContext = createContext(null);

function cartReducer(state, action) {
  switch (action.type) {
    case 'ADD': {
      const existing = state.find(i => i._id === action.product._id);
      if (existing) {
        return state.map(i =>
          i._id === action.product._id
            ? { ...i, qty: i.qty + action.qty }
            : i
        );
      }
      return [...state, { ...action.product, qty: action.qty }];
    }
    case 'REMOVE':
      return state.filter(i => i._id !== action.id);
    case 'UPDATE_QTY':
      return state.map(i =>
        i._id === action.id ? { ...i, qty: Math.max(1, action.qty) } : i
      );
    case 'SYNC':
      return action.cart;
    case 'CLEAR':
      return [];
    default:
      return state;
  }
}

function loadCart() {
  try {
    const saved = localStorage.getItem('gw_cart');
    return saved ? JSON.parse(saved) : [];
  } catch {
    return [];
  }
}

export function CartProvider({ children }) {
  const [cart, dispatch] = useReducer(cartReducer, [], loadCart);
  const [syncNotes, setSyncNotes] = useState([]);
  const [cartDrawerOpen, setCartDrawerOpen] = useState(false);

  const openCartDrawer = () => setCartDrawerOpen(true);
  const closeCartDrawer = () => setCartDrawerOpen(false);

  // Keep a ref so syncCart always reads the latest cart without stale closure
  const cartRef = useRef(cart);
  useEffect(() => { cartRef.current = cart; }, [cart]);

  useEffect(() => {
    localStorage.setItem('gw_cart', JSON.stringify(cart));
  }, [cart]);

  // Stable function — never re-created, reads cart via ref
  const syncCart = useCallback(async () => {
    const current = cartRef.current;
    if (current.length === 0) return;
    try {
      const { data: products } = await api.get('/products');
      const notes = [];
      const updated = [];

      for (const item of current) {
        const live = products.find(p => String(p._id) === String(item._id));
        if (!live) continue; // deleted product — silently drop

        if (!live.inStock) {
          notes.push({ type: 'removed', name: item.name });
          continue; // out of stock — drop from cart
        }

        const livePrice = parseFloat(live.price);
        const cartPrice = parseFloat(item.price);
        if (livePrice !== cartPrice) {
          notes.push({ type: 'price', name: item.name, oldPrice: cartPrice, newPrice: livePrice });
          updated.push({ ...item, price: livePrice });
        } else {
          updated.push(item);
        }
      }

      // Always sync the cart (removes out-of-stock items even with no price notes)
      dispatch({ type: 'SYNC', cart: updated });
      if (notes.length > 0) setSyncNotes(notes);
    } catch {
      // silently fail — don't block checkout
    }
  }, []); // no deps — reads cart via ref

  const addToCart = (product, qty = 1) => {
    dispatch({ type: 'ADD', product, qty });
    setCartDrawerOpen(true);
  };

  const addToCartSilent = (product, qty = 1) => {
    dispatch({ type: 'ADD', product, qty });
  };

  const removeFromCart = (id) =>
    dispatch({ type: 'REMOVE', id });

  const updateQty = (id, qty) =>
    dispatch({ type: 'UPDATE_QTY', id, qty });

  const clearCart = () =>
    dispatch({ type: 'CLEAR' });

  const dismissSyncNotes = () => setSyncNotes([]);

  const itemCount = cart.reduce((sum, i) => sum + i.qty, 0);
  const subtotal  = cart.reduce((sum, i) => sum + i.price * i.qty, 0);

  return (
    <CartContext.Provider value={{ cart, addToCart, addToCartSilent, removeFromCart, updateQty, clearCart, syncCart, syncNotes, dismissSyncNotes, itemCount, subtotal, cartDrawerOpen, openCartDrawer, closeCartDrawer }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  return useContext(CartContext);
}
